import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import StatusBadge from "@/components/StatusBadge";
import RunRouteMap from "@/components/admin/RunRouteMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CreditCard, Truck, Map, PaintBucket, Ban } from "lucide-react";
import { toast } from "sonner";

const CANBERRA_TOWNS = ["canberra", "goulburn", "bungendore", "tarago", "crookwell", "gunning", "gundaroo", "queanbeyan"];
const JINDY_TOWNS = ["cooma", "jindabyne", "east jindabyne", "dalgety", "berridale", "hilltop", "hill top", "avonside", "beloka", "moonbah", "maffra", "bobundara", "rocky plain", "nimmo", "eucumbene", "braemar bay", "kalkite"];

const isACT = (a) => {
  const pc = parseInt(a?.postcode, 10);
  return (pc >= 2600 && pc <= 2618) || (pc >= 2900 && pc <= 2914);
};
const inList = (a, list) => {
  const c = (a?.city || "").toLowerCase();
  return c && list.some((t) => c.includes(t));
};

export const suggestRun = (o) => {
  const cbr = (a) => inList(a, CANBERRA_TOWNS) || isACT(a);
  if (cbr(o.pickup) || cbr(o.dropoff)) return "canberra";
  if (inList(o.pickup, JINDY_TOWNS) || inList(o.dropoff, JINDY_TOWNS)) return "jindabyne";
  return "other";
};

const LANES = [
  { id: "quotes", label: "Quotes", hint: "Awaiting payment or account allocation" },
  { id: "paid", label: "Paid", hint: "Ready to place on a run" },
  { id: "canberra", label: "Canberra Run", hint: "Canberra, Goulburn, Bungendore…" },
  { id: "jindabyne", label: "Jindabyne Run", hint: "Cooma, Jindabyne, Berridale…" },
  { id: "other", label: "Other Run", hint: "One-offs & everything else" },
];

const laneOf = (o) => {
  if (o.status === "pending_payment") return "quotes";
  if (o.delivery_run && LANES.some((l) => l.id === o.delivery_run)) return o.delivery_run;
  return "paid";
};

const ON_BOARD = ["pending_payment", "paid", "confirmed", "assigned", "picked_up", "in_transit"];

const RUN_IDS = ["canberra", "jindabyne", "other"];

const QUICK_ACTIONS = {
  paid: [{ v: "picked_up", l: "Picked up" }],
  confirmed: [{ v: "picked_up", l: "Picked up" }],
  assigned: [{ v: "picked_up", l: "Picked up" }],
  picked_up: [{ v: "in_transit", l: "In transit" }, { v: "delivered", l: "Delivered" }],
  in_transit: [{ v: "delivered", l: "Delivered" }],
};

function Bubble({ order, index, onOpen, seq, carried, onQuick, onCancel }) {
  const isPaint = order.kind === "paint";
  const actions = isPaint ? [{ v: "delivered", l: "Delivered" }] : (QUICK_ACTIONS[order.status] || []);
  return (
    <Draggable draggableId={order.id} index={index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
          onClick={() => onOpen(order)}
          data-testid={`board-bubble-${order.tracking_number}`}
          className={`cursor-grab rounded-2xl border p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing ${isPaint ? "bg-kilted-gold/10" : "bg-white"} ${snapshot.isDragging ? "rotate-2 border-kilted-gold shadow-lg ring-2 ring-kilted-gold/40" : carried ? "border-amber-300" : isPaint ? "border-kilted-gold/50" : "border-border"}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 font-heading text-sm font-bold text-kilted-green">
              {seq != null && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-kilted-green text-[10px] font-bold text-white" data-testid={`board-seq-${order.tracking_number}`}>{seq}</span>}
              {isPaint && <PaintBucket className="h-3.5 w-3.5 text-kilted-gold" />}{order.tracking_number}
            </span>
            <span className="flex items-center gap-1.5 text-xs font-semibold">${order.price.total.toFixed(2)}
              {!isPaint && onCancel && (
                <button type="button" onClick={(e) => { e.stopPropagation(); onCancel(order); }} title="Cancel this order" data-testid={`board-cancel-${order.tracking_number}`}
                  className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-700"><Ban className="h-3.5 w-3.5" /></button>
              )}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground">{order.pickup.city} → {order.dropoff.city}</p>
          <p className="truncate text-xs text-muted-foreground">{order.customer_name}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <StatusBadge status={order.status} />
            {order.hold && <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700" title={`On hold — ${order.hold.cancels_30d} cancellations in 30 days. Confirm by phone and release on the order page.`} data-testid={`board-hold-${order.tracking_number}`}>hold</span>}
            {carried && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`board-carried-${order.tracking_number}`}>carried over</span>}
            {isPaint && <span className="rounded-full bg-kilted-gold/30 px-1.5 py-0.5 text-[10px] font-bold text-kilted-green" data-testid={`board-paint-${order.tracking_number}`}>paint</span>}
            {order.billing === "account" && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"><CreditCard className="mr-0.5 inline h-2.5 w-2.5" />account</span>}
            {order.payment?.xero_sync === "invoiced" ? (
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`xero-synced-${order.tracking_number}`} title={order.payment?.xero_invoice_number || ""}>✓ Xero</span>
            ) : order.payment?.status === "on_account" && !order.payment?.billed ? (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`xero-pending-${order.tracking_number}`}>Xero pending</span>
            ) : null}
            {order.driver_name && <span className="rounded-full bg-kilted-green/10 px-1.5 py-0.5 text-[10px] font-medium text-kilted-green"><Truck className="mr-0.5 inline h-2.5 w-2.5" />{order.driver_name.split(" ")[0]}</span>}
          </div>
          {actions.length > 0 && (
            <div className="mt-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
              {actions.map((a) => (
                <button key={a.v} type="button" data-testid={`quick-${a.v}-${order.tracking_number}`}
                  onClick={() => onQuick(order, a.v, a.l)}
                  title={`Mark ${a.l.toLowerCase()} without opening the order`}
                  className={`flex-1 rounded-full border px-2 py-1 text-[10px] font-bold transition-colors ${a.v === "delivered"
                    ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    : "border-kilted-green/40 text-kilted-green hover:bg-kilted-green/10"}`}>
                  {a.l} →
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

export default function OrdersBoard({ orders, onMove, onOpen, runDate, onResequence, onQuick, onCancel }) {
  const [suggested, setSuggested] = useState(null);
  const [mapLane, setMapLane] = useState(null);
  const board = orders.filter((o) => ON_BOARD.includes(o.status));
  const isRun = (id) => RUN_IDS.includes(id);
  const byLane = {};
  const lastActivity = (o) => o.status_history?.[o.status_history.length - 1]?.timestamp || o.created_at || "";
  LANES.forEach((l) => {
    let list = board.filter((o) => laneOf(o) === l.id);
    if (isRun(l.id)) {
      list = list.filter((o) => !o.run_date || o.run_date <= runDate)
        .sort((a, b) => (a.run_sequence ?? 999) - (b.run_sequence ?? 999));
    } else {
      list = [...list].sort((a, b) => lastActivity(b).localeCompare(lastActivity(a)));
    }
    byLane[l.id] = list;
  });

  const onDragStart = (start) => {
    const o = board.find((x) => x.id === start.draggableId);
    if (o?.kind === "paint") setSuggested("jindabyne");
    else if (o && o.status !== "pending_payment") setSuggested(suggestRun(o));
    else if (o) setSuggested("paid");
  };

  const onDragEnd = (result) => {
    setSuggested(null);
    const { destination, source, draggableId } = result;
    if (!destination) { toast.info("Drop cancelled — release the bubble over a lane"); return; }
    if (destination.droppableId === source.droppableId) {
      if (isRun(destination.droppableId) && destination.index !== source.index) {
        const ids = byLane[destination.droppableId].map((o) => o.id);
        const [moved] = ids.splice(source.index, 1);
        ids.splice(destination.index, 0, moved);
        onResequence(destination.droppableId, ids);
      }
      return;
    }
    onMove(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="mt-4 flex gap-3 overflow-x-auto pb-4" data-testid="orders-board">
        {LANES.map((lane) => (
          <div key={lane.id} data-testid={`board-lane-${lane.id}`}
            className={`flex min-h-[300px] w-full min-w-[200px] max-w-[280px] flex-1 flex-col rounded-xl border p-2 transition-colors ${
              suggested === lane.id ? "border-kilted-gold bg-kilted-gold/10 ring-2 ring-kilted-gold/50" : "border-border bg-secondary/30"}`}>
            <div className="px-1.5 pb-2 pt-1">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 text-sm font-bold text-kilted-green">
                  {lane.label}
                  {RUN_IDS.includes(lane.id) && (
                    <button onClick={() => setMapLane(lane)} data-testid={`board-map-${lane.id}`} title="View route map"
                      className="rounded p-0.5 text-kilted-gold transition-colors hover:bg-kilted-gold/20">
                      <Map className="h-3.5 w-3.5" />
                    </button>
                  )}
                </h3>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-muted-foreground" data-testid={`board-count-${lane.id}`}>{byLane[lane.id].length}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">{lane.hint}</p>
              {suggested === lane.id && <p className="mt-0.5 text-[10px] font-semibold text-kilted-gold">Suggested lane</p>}
            </div>
            <Droppable droppableId={lane.id} isDropDisabled={lane.id === "quotes"}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}
                  className={`flex max-h-[62vh] flex-1 flex-col gap-2 overflow-y-auto rounded-lg p-0.5 transition-colors ${snapshot.isDraggingOver ? "bg-kilted-green/10 ring-1 ring-kilted-green" : ""}`}>
                  {byLane[lane.id].map((o, i) => (
                    <Bubble key={o.id} order={o} index={i} onOpen={onOpen} onQuick={onQuick} onCancel={onCancel}
                      seq={RUN_IDS.includes(lane.id) ? i + 1 : null}
                      carried={RUN_IDS.includes(lane.id) && o.run_date && o.run_date < new Date().toLocaleDateString("sv-SE")} />
                  ))}
                  {provided.placeholder}
                  {byLane[lane.id].length === 0 && <p className="mt-6 text-center text-xs text-muted-foreground/60">Drop orders here</p>}
                </div>
              )}
            </Droppable>
          </div>
        ))}
      </div>
      <Dialog open={!!mapLane} onOpenChange={(v) => { if (!v) setMapLane(null); }}>
        <DialogContent className="max-w-2xl" data-testid="run-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{mapLane?.label} — stop order</DialogTitle>
          </DialogHeader>
          {mapLane && <RunRouteMap orders={byLane[mapLane.id]} />}
          <p className="text-xs text-muted-foreground">Numbered in the driver's stop order — drag bubbles up/down in the lane to change it.</p>
        </DialogContent>
      </Dialog>
    </DragDropContext>
  );
}
